import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Fuse from "fuse.js";
import { useMemo, useRef, useState } from "react";
import GraphVisualization from "../../../../components/graph-visualization";
import { useApiMutation, useApiQuery } from "../../../../hooks";
import type { ApiConfig, Graph, Test } from "../../../../types";

interface DependenciesTabProps {
  depGraph: Graph;
  testId: string;
  onDependenciesUpdate?: () => void;
}

export function DependenciesTab({ depGraph, testId, onDependenciesUpdate }: DependenciesTabProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTestIds, setSelectedTestIds] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch all tests for selection
  const { data: tests, isLoading: isLoadingTests } = useApiQuery<Test[]>("/tests");

  // Fetch app configs to match with tests
  const { data: apiConfig } = useApiQuery<ApiConfig>("/config");

  // Fetch current test to get existing dependencies
  const { data: currentTest } = useApiQuery<Test>(`/tests/${testId}`);

  // Mutation for updating test dependencies
  const updateDependenciesMutation = useApiMutation<{ test_dependency_ids: string[] }, Test>(
    "PUT",
    `/tests/${testId}/dependencies`,
    {
      onSuccess: () => {
        onDependenciesUpdate?.();
        setIsDropdownOpen(false);
        setSearchQuery("");
      },
    },
  );

  // Create Fuse instance for fuzzy search
  const fuse = useMemo(() => {
    if (!tests) return null;
    return new Fuse(tests, {
      keys: ["name", "id"],
      threshold: 0.3,
      includeScore: true,
    });
  }, [tests]);

  // Filter tests based on search query and sort with selected tests at top
  const filteredTests = useMemo(() => {
    if (!tests) return [];

    let filtered = tests;

    if (searchQuery.trim() && fuse) {
      filtered = fuse.search(searchQuery, { limit: 50 }).map(({ item }) => item);
    }

    // Sort with selected tests at the top, then alphabetically
    return filtered.sort((a, b) => {
      const aSelected = selectedTestIds.includes(a.id);
      const bSelected = selectedTestIds.includes(b.id);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      return a.name.localeCompare(b.name);
    });
  }, [tests, searchQuery, fuse, selectedTestIds]);

  const handleEditGraph = () => {
    // Initialize with current test's dependencies
    if (currentTest?.test_dependency_ids) {
      setSelectedTestIds(currentTest.test_dependency_ids);
    }
    setIsDropdownOpen(true);
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
    setSearchQuery("");
  };

  const handleTestToggle = (testId: string) => {
    setSelectedTestIds((prev) =>
      prev.includes(testId) ? prev.filter((id) => id !== testId) : [...prev, testId],
    );
  };

  const handleSave = () => {
    updateDependenciesMutation.mutate({
      test_dependency_ids: selectedTestIds,
    });
  };

  if (!depGraph) {
    return <Typography>No dependency graph available for this test</Typography>;
  }

  return (
    <Box sx={{ position: "relative" }}>
      <GraphVisualization
        nodes={depGraph.nodes}
        edges={depGraph.edges}
        onEditGraph={handleEditGraph}
        tests={tests}
        appConfigs={apiConfig?.configs}
      />

      {isDropdownOpen && (
        <ClickAwayListener onClickAway={handleCloseDropdown}>
          <Paper
            ref={dropdownRef}
            elevation={8}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 500,
              height: "60vh",
              zIndex: 1000,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                Change Dependencies
              </Typography>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0.5,
                    fontSize: "0.875rem",
                    "& fieldset": {
                      borderColor: "divider",
                    },
                    "&:hover fieldset": {
                      borderColor: "text.secondary",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: 1,
                    },
                  },
                  "& .MuiInputBase-input": {
                    py: 0.75,
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>

            <Box sx={{ height: "calc(60vh - 140px)", overflow: "auto" }}>
              {isLoadingTests ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2">Loading tests...</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredTests.map((test) => (
                    <ListItem key={test.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleTestToggle(test.id)}
                        dense
                        sx={{ px: 2, py: 0.5 }}
                      >
                        <Checkbox
                          checked={selectedTestIds.includes(test.id)}
                          onChange={() => handleTestToggle(test.id)}
                          edge="start"
                          size="small"
                        />
                        <ListItemText
                          primary={<Typography variant="body2">{test.name}</Typography>}
                          secondary={(() => {
                            const appConfig = apiConfig?.configs?.find(
                              (c) => c.id === test.app_config_id,
                            );
                            return appConfig ? (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight="medium"
                                  sx={{
                                    fontSize: "0.7rem",
                                    display: "block",
                                  }}
                                >
                                  {appConfig.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "0.65rem",
                                    display: "block",
                                  }}
                                >
                                  {appConfig.base_url ?? "about:blank"}
                                </Typography>
                              </Box>
                            ) : null;
                          })()}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Save Button Footer */}
            <Box
              sx={{
                p: 1,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={updateDependenciesMutation.isPending}
                size="small"
                sx={{
                  minWidth: 80,
                  height: 32,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius: 0.5,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {updateDependenciesMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
}
